/*
 * 08 Left - "See it on a wall" (Ryan 2026-10-19)
 * ---------------------------------------------------------------------------
 * The print the shopper is looking at, hung in one of Ryan's three rooms
 * (concrete wall / white sunroom / stone wall), drawn live on a canvas: the
 * room, a little drop shadow, the print, then his light and shadow layers
 * from the PSD on top. One print per room, never three. No sizes, no frames.
 *
 * Rooms are cut by ~/Downloads/08-left-wall-mockups/build-rooms.py; their
 * manifest + asset URLs come from snippets/wall-08l.liquid (#wall08lRooms).
 *
 * The art is the product image: a 2:3 portrait (or 3:2 landscape: Windows
 * posters, Towerfuls) on a transparent or white square. The empty sides are
 * trimmed by reading pixels (08left.com/cdn is the same origin and sends CORS
 * headers). Portrait art hangs in each room's "tall" version, landscape art in
 * its "wide" version (same long side on the wall).
 *
 * Which products get the link is decided in the snippet (Liquid), so it never
 * appears and then vanishes. Nothing loads until the link is tapped.
 */
(function () {
  'use strict';
  var VERSION = '2026-10-19.4';
  if (window.__wall08l) return;
  window.__wall08l = VERSION;

  // Drop shadow under every print, on top of the one in each PSD (which is
  // barely there at this crop). Ryan: "make sure a little drop shadow too".
  var LIFT = { blur: 0.040, offset: 0.018, opacity: 0.45 };  // x print long side

  var btn = document.querySelector('[data-wall08l-open]');
  var data = document.getElementById('wall08lRooms');
  if (!btn || !data) return;
  var cfg;
  try { cfg = JSON.parse(data.textContent); } catch (e) { return; }
  if (!cfg || !cfg.rooms || !cfg.rooms.length) return;

  var isTower = btn.getAttribute('data-tower') === '1';
  var order = isTower ? ['concrete', 'sunroom', 'marble'] : ['sunroom', 'marble', 'concrete'];
  var rooms = order.map(function (k) {
    for (var i = 0; i < cfg.rooms.length; i++) if (cfg.rooms[i].key === k) return cfg.rooms[i];
  }).filter(Boolean);

  // ---- images ------------------------------------------------------------
  var imgCache = {};
  function loadImg(src) {
    if (!imgCache[src]) {
      imgCache[src] = new Promise(function (res, rej) {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.decoding = 'async';
        im.onload = function () { res(im); };
        im.onerror = function () { rej(new Error('image failed: ' + src)); };
        im.src = src;
      });
    }
    return imgCache[src];
  }
  function sized(src, w) {
    try {
      var u = new URL(src, location.href);
      u.searchParams.set('width', String(w));
      u.searchParams.delete('height'); u.searchParams.delete('crop');
      return u.href;
    } catch (e) { return src; }
  }
  function asset(name) { return cfg.assets[name]; }

  // Bounding box of the art: the opaque part, or on a fully opaque image the
  // part that isn't white. Returns null unless it is 2:3 or 3:2 art.
  function trim(im) {
    var w = im.naturalWidth, h = im.naturalHeight;
    if (!w || !h) return null;
    var c = document.createElement('canvas');
    var s = Math.min(1, 400 / Math.max(w, h));
    c.width = Math.max(1, Math.round(w * s)); c.height = Math.max(1, Math.round(h * s));
    var x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(im, 0, 0, c.width, c.height);
    var px;
    try { px = x.getImageData(0, 0, c.width, c.height).data; } catch (e) { return null; }
    var n = c.width * c.height, opaque = true;
    for (var k = 0; k < n; k += 7) if (px[k * 4 + 3] < 250) { opaque = false; break; }
    function isArt(i) {
      if (!opaque) return px[i + 3] > 24;
      return px[i] < 242 || px[i + 1] < 242 || px[i + 2] < 242;   // not white margin
    }
    var minX = c.width, minY = c.height, maxX = -1, maxY = -1;
    for (var yy = 0; yy < c.height; yy++) {
      for (var xx = 0; xx < c.width; xx++) {
        if (isArt((yy * c.width + xx) * 4)) {
          if (xx < minX) minX = xx; if (xx > maxX) maxX = xx;
          if (yy < minY) minY = yy; if (yy > maxY) maxY = yy;
        }
      }
    }
    if (maxX < 0) return null;
    var bw = (maxX - minX + 1) / s, bh = (maxY - minY + 1) / s;
    var ratio = bh / bw;
    var shape = ratio >= 1.25 && ratio <= 1.8 ? 'tall' : ratio >= 0.55 && ratio <= 0.8 ? 'wide' : null;
    if (!shape) return null;
    // Pull in half a sample on each side so no transparent fringe survives.
    var pad = 0.5 / s;
    return { x: minX / s + pad, y: minY / s + pad, w: bw - 2 * pad, h: bh - 2 * pad, ratio: ratio, shape: shape };
  }

  function artFrom(src, w) {
    return loadImg(sized(src, w)).then(function (im) {
      var box = trim(im);
      if (!box) throw new Error('not 2:3 or 3:2 art');
      return { im: im, box: box };
    });
  }

  // The art to hang: whatever the main image shows right now (a colour or
  // White Line variant), else the product's first image.
  function currentArt() {
    var main = document.getElementById('olpMainImg');
    var first = btn.getAttribute('data-art');
    // Only trust the main image if it actually loaded; a broken one keeps
    // reporting the previous picture's URL.
    var cur = main && main.complete && main.naturalWidth > 0 && (main.currentSrc || main.src);
    var p = cur ? artFrom(cur, 1200) : Promise.reject();
    return p.catch(function () { return artFrom(first, 1200); });
  }

  // ---- drawing -----------------------------------------------------------
  // Where the print goes: the room's spot for this shape, with the art's own
  // proportions (same centre, same long side).
  function printRect(v, art) {
    var n = v.print, cx = n.x + n.w / 2, cy = n.y + n.h / 2, long = Math.max(n.w, n.h);
    var w = art.box.shape === 'tall' ? long / art.box.ratio : long;
    var h = art.box.shape === 'tall' ? long : long * art.box.ratio;
    return { x: cx - w / 2, y: cy - h / 2, w: w, h: h };
  }

  function draw(room, art) {
    var v = room[art.box.shape];
    var need = [loadImg(asset(v.room))];
    v.layers.forEach(function (l) { need.push(loadImg(asset(l.src))); });
    return Promise.all(need).then(function (ims) {
      var S = room.size, P = printRect(v, art);
      var c = document.createElement('canvas');
      c.width = S; c.height = S;
      var x = c.getContext('2d');
      x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
      x.drawImage(ims[0], 0, 0, S, S);
      hang(x, P, room.psdShadow, art);

      // Ryan's light and shadow, on the print only (the builder continued it
      // past the PSD's portrait spot so wide prints are covered too).
      var pw = Math.ceil(P.w), ph = Math.ceil(P.h);
      v.layers.forEach(function (l, i) {
        var lc = document.createElement('canvas');
        lc.width = pw; lc.height = ph;
        lc.getContext('2d').drawImage(ims[i + 1], -P.x, -P.y, S, S);
        x.save();
        x.globalCompositeOperation = l.blend || 'source-over';
        x.globalAlpha = l.opacity;
        x.drawImage(lc, P.x, P.y);
        x.restore();
      });
      return c;
    });
  }

  function hang(x, P, psd, art) {
    // Shadows follow the light in each PSD (its drop-shadow angle).
    var dx = psd.dx || -1, dy = psd.dy || 1, len = Math.hypot(dx, dy) || 1;
    dx /= len; dy /= len;

    var L = Math.max(P.w, P.h);
    // 1. the soft lift off the wall
    shadowRect(x, P, {
      blur: LIFT.blur * L, ox: dx * LIFT.offset * L, oy: Math.max(dy, 0.8) * LIFT.offset * L,
      alpha: LIFT.opacity
    });
    // 2. the tight contact shadow Ryan set in the PSD
    if (psd.opacity) {
      shadowRect(x, P, { blur: Math.max(psd.size, 1.5) * 2, ox: psd.dx, oy: psd.dy, alpha: psd.opacity });
    }

    // 3. the print
    var b = art.box;
    x.drawImage(art.im, b.x, b.y, b.w, b.h, P.x, P.y, P.w, P.h);
  }

  // A blurred rectangle's shadow with the rectangle itself kept off-canvas,
  // so only the shadow lands (the print covers the middle anyway).
  function shadowRect(x, P, o) {
    var FAR = 100000;
    x.save();
    x.shadowColor = 'rgba(0,0,0,' + o.alpha + ')';
    x.shadowBlur = o.blur;
    x.shadowOffsetX = o.ox + FAR;
    x.shadowOffsetY = o.oy;
    x.fillStyle = '#000';
    x.fillRect(P.x - FAR, P.y, P.w, P.h);
    x.restore();
  }

  // ---- viewer ------------------------------------------------------------
  var ui, stage, caption, count, prevB, nextB, closeB, idx = 0, artP = null, drawn = {}, lastFocus = null, token = 0;

  function build() {
    ui = document.createElement('div');
    ui.className = 'w8';
    ui.setAttribute('role', 'dialog');
    ui.setAttribute('aria-modal', 'true');
    ui.setAttribute('aria-label', 'This print on a wall');
    ui.innerHTML =
      '<button type="button" class="w8__close" aria-label="Close">&times;</button>' +
      '<div class="w8__body">' +
        '<button type="button" class="w8__nav w8__prev" aria-label="Previous room">&#8249;</button>' +
        '<div class="w8__stage" aria-live="polite"><div class="w8__wait">Hanging it up&hellip;</div></div>' +
        '<button type="button" class="w8__nav w8__next" aria-label="Next room">&#8250;</button>' +
      '</div>' +
      '<div class="w8__foot"><span class="w8__name"></span><span class="w8__count"></span></div>';
    document.body.appendChild(ui);
    stage = ui.querySelector('.w8__stage');
    caption = ui.querySelector('.w8__name');
    count = ui.querySelector('.w8__count');
    prevB = ui.querySelector('.w8__prev');
    nextB = ui.querySelector('.w8__next');
    closeB = ui.querySelector('.w8__close');
    if (rooms.length < 2) { prevB.hidden = true; nextB.hidden = true; }

    closeB.addEventListener('click', close);
    prevB.addEventListener('click', function () { go(idx - 1); });
    nextB.addEventListener('click', function () { go(idx + 1); });
    ui.addEventListener('click', function (e) {
      if (e.target === ui || e.target.classList.contains('w8__body') || e.target.classList.contains('w8__foot')) close();
    });
    ui.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(idx - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
      else if (e.key === 'Tab') trapTab(e);
    });
    var sx = null, sy = null;
    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { sx = null; return; }
      sx = e.touches[0].clientX; sy = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (sx === null) return;
      var t = e.changedTouches[0], ddx = t.clientX - sx, ddy = t.clientY - sy;
      sx = null;
      if (Math.abs(ddx) > 40 && Math.abs(ddx) > Math.abs(ddy) * 1.3) go(idx + (ddx < 0 ? 1 : -1));
    });
  }

  function trapTab(e) {
    var f = [closeB, prevB, nextB].filter(function (b) { return !b.hidden; });
    var i = f.indexOf(document.activeElement);
    if (e.shiftKey && (i <= 0)) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
  }

  function open() {
    if (!ui) build();
    var a = document.activeElement;
    lastFocus = a && a !== document.body ? a : btn;
    // The art can change between opens (variant pills), so draw fresh.
    artP = currentArt();
    drawn = {};
    idx = 0;
    stage.innerHTML = '<div class="w8__wait">Hanging it up&hellip;</div>';
    ui.classList.add('is-open');
    document.documentElement.classList.add('w8-lock');
    closeB.focus({ preventScroll: true });
    show(0);
    // Warm the other rooms in the background.
    artP.then(function () { rooms.forEach(function (r, i) { if (i) render(i); }); }, function () {});
  }

  function close() {
    if (!ui) return;
    ui.classList.remove('is-open');
    document.documentElement.classList.remove('w8-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  function render(i) {
    if (!drawn[i]) {
      drawn[i] = artP.then(function (art) { return draw(rooms[i], art); });
      drawn[i].catch(function () { delete drawn[i]; });
    }
    return drawn[i];
  }

  function go(i) {
    var n = rooms.length;
    show(((i % n) + n) % n);
  }

  function show(i) {
    idx = i;
    var my = ++token;
    caption.textContent = rooms[i].name;
    count.textContent = (i + 1) + ' / ' + rooms.length;
    stage.classList.add('is-busy');
    render(i).then(function (c) {
      if (my !== token) return;
      c.className = 'w8__canvas';
      c.setAttribute('role', 'img');
      c.setAttribute('aria-label', btn.getAttribute('data-title') + ' on a wall: ' + rooms[i].name);
      var old = stage.querySelector('.w8__canvas.is-on');
      stage.appendChild(c);
      // force layout so the fade runs
      void c.offsetWidth;
      c.classList.add('is-on');
      stage.classList.remove('is-busy');
      var wait = stage.querySelector('.w8__wait'); if (wait) wait.remove();
      if (old && old !== c) setTimeout(function () { if (old.parentNode && !old.classList.contains('is-on')) old.remove(); }, 400);
      if (old && old !== c) old.classList.remove('is-on');
    }, function (err) {
      if (my !== token) return;
      stage.classList.remove('is-busy');
      stage.innerHTML = '<div class="w8__wait">Sorry, this one won\u2019t hang right now. Try again in a minute.</div>';
      if (window.console) console.warn('[wall-08l]', err && err.message);
    });
  }

  // ---- the button ----------------------------------------------------------

  btn.addEventListener('click', function (e) { e.preventDefault(); open(); });
  if (window.console) console.info('[wall-08l] ' + VERSION);
})();
